import { memo } from "react";
import styled from "styled-components";

import ToolTip from "../tooltip/ToolTip";

// 定数として利用可能なサイズを定義
const AVATAR_SIZES = {
  XS: 12,
  SM: 32,
  MD: 40,
  LG: 48,
  XL: 96,
  XXL: 144,
} as const;

interface AvatarContainerProps {
  $size: number;
  $editable: boolean;
  $selectable: boolean;
  $selected: boolean;
  $shadow: "default" | "wide";
}

const AvatarContainer = styled.div<AvatarContainerProps>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 43% 57% 43% 57% / 57% 43% 57% 43%;
  transform: rotate(-19deg);
  /* "wide" は白縁を消し、影だけでふわっと浮かせる */
  border: ${({ $shadow }) =>
    $shadow === "wide" ? "0.5px solid transparent" : "1px solid #fff"};
  box-shadow: ${({ $shadow }) =>
    $shadow === "wide"
      ? "16px 16px 40px 0px rgba(0, 0, 0, 0.25)"
      : "2px 4px 8px 0px #a7a8a9"};
  overflow: hidden;
  position: relative;
  cursor: ${({ $editable, $selectable }) =>
    $editable || $selectable ? "pointer" : "default"};

  /* 選択中は常時、青 outline + 薄青背景 + 青の drop shadow。
   * グローバルメニューのアバター編集ホバーと同じ視覚言語に揃え、
   * かつ影も同系統の青で「選ばれている感」を補強する。 */
  ${({ $selected }) =>
    $selected &&
    `
    background-color: var(--soremo-bg-blue);
    outline: 3px solid var(--soremo-blue);
    box-shadow: 2px 4px 8px 0px rgba(0, 154, 206, 0.5);
  `}

  &:hover {
    ${({ $editable, $selectable }) =>
      ($editable || $selectable) &&
      `
      background-color: var(--soremo-bg-blue);
      outline: 3px solid var(--soremo-blue);
      box-shadow: 2px 4px 8px 0px rgba(0, 154, 206, 0.5);
    `}
  }
`;

interface AvatarImageProps {
  $src: string;
}

const AvatarImage = styled.div<AvatarImageProps>`
  width: 100%;
  height: 100%;
  background-image: url(${({ $src }) => $src});
  background-size: cover;
  background-position: center center;
  background-repeat: no-repeat;
  transform: rotate(19deg);
`;

const hashStringToHue = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  return ((hash % 360) + 360) % 360;
};

const getColorFromName = (name?: string): string => {
  if (!name) return "var(--soremo-gray)";
  const hue = hashStringToHue(name);
  return `hsl(${hue}, 15%, 80%)`;
};

interface DefaultAvatarIconProps {
  $bgColor: string;
  $iconSize: number;
}

const DefaultAvatarIcon = styled.div<DefaultAvatarIconProps>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ $bgColor }) => $bgColor};
  transform: rotate(19deg);

  .material-symbols-rounded {
    color: #fff;
    font-size: ${({ $iconSize }) => $iconSize}px;
    user-select: none;
  }
`;

const OnlineBadge = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  width: 10px;
  height: 10px;
  background-color: #2cc84d;
  border: 1px solid #fff;
  border-radius: 50%;
  z-index: 1;
`;

const AvatarWrapper = styled.div`
  position: relative;
  display: inline-block;
  flex-shrink: 0;
`;

interface AvatarProps {
  /** アバターのサイズ（px） */
  size?: number;
  /** 画像のURL */
  src?: string;
  /** オンライン状態を表示するかどうか */
  isOnline?: boolean;
  /** 編集可能かどうか（クリックで編集モーダルを開く想定）。ホバー時に青 outline が出る。 */
  editable?: boolean;
  /** 選択可能なリスト要素の一つとして振る舞う場合 true。ホバー時に青 outline が出る。 */
  selectable?: boolean;
  /** 選択中フラグ。常時、青 outline と薄青背景を表示する。 */
  selected?: boolean;
  /** クリック時のハンドラ */
  onClick?: () => void;
  /** 追加のクラス名 */
  className?: string;
  /** ホバー時に表示するツールチップのテキスト */
  tooltipText?: string;
  /** ツールチップの表示位置 */
  tooltipPlacement?: "top" | "bottom" | "left" | "right" | "top-start" | "top-end" | "bottom-start" | "bottom-end" | "left-start" | "left-end" | "right-start" | "right-end";
  /** ツールチップのオフセット [x軸, y軸] */
  tooltipOffset?: [number, number];
  /** ユーザー表示名（フォールバックアイコンの背景色をハッシュ生成するために使用） */
  name?: string;
  /** 影の variant。"wide" は広くソフトな影（公開プロフィール等、特別に見せたい文脈用）。 */
  shadow?: "default" | "wide";
}

const Avatar = memo<AvatarProps>(({
  size = AVATAR_SIZES.LG,
  src,
  isOnline = false,
  editable = false,
  selectable = false,
  selected = false,
  onClick,
  className,
  tooltipText,
  tooltipPlacement = "top",
  tooltipOffset = [0, 8],
  name,
  shadow = "default",
}) => {
  const hasSrc = !!src;

  // editable / selectable のどちらかでも onClick を受け付ける。
  const clickProps = (editable || selectable) && onClick ? { onClick } : {};

  const avatarContent = (
    <AvatarWrapper className={className} {...clickProps}>
      {isOnline && <OnlineBadge />}
      <AvatarContainer
        $size={size}
        $editable={editable}
        $selectable={selectable}
        $selected={selected}
        $shadow={shadow}
      >
        {hasSrc ? (
          <AvatarImage $src={src} />
        ) : (
          <DefaultAvatarIcon $bgColor={getColorFromName(name)} $iconSize={Math.round(size * 0.55)}>
            <span className="material-symbols-rounded">person</span>
          </DefaultAvatarIcon>
        )}
      </AvatarContainer>
    </AvatarWrapper>
  );

  // tooltipTextがある場合はToolTipでラップ
  if (tooltipText) {
    return (
      <ToolTip description={tooltipText} placement={tooltipPlacement} offset={tooltipOffset}>
        {avatarContent}
      </ToolTip>
    );
  }

  return avatarContent;
});

Avatar.displayName = 'Avatar';

// 利用可能なサイズを外部から参照できるようにする
const AvatarWithStatics = Object.assign(Avatar, {
  SIZES: AVATAR_SIZES,
});

export default AvatarWithStatics;
